import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { errorText } from '../lib/errors';
import { fmtDateTime, fmtWeekday } from '../lib/format';
import { useToast } from '../lib/toast';
import type { Invite, InviteStatus } from '../lib/types';
import { QrCanvas } from './ui';
import { Modal } from './passes';

/** The join link: the token stays in the #fragment, so it never reaches a server log. */
export const joinUrl = (token: string) => `${location.origin}/join#${token}`;

/** The WhatsApp message with a family's one-time join link. */
export function inviteText(inv: Invite): string {
  return [
    `নমস্কার ${inv.name.split(' ')[0]} 🙏`,
    `Your ONE10 food booking for flat ${inv.flat} is confirmed${inv.paid_cash ? ' (paid in cash)' : ''}.`,
    '',
    "Tap to get your meal passes, live updates and meal alerts on your phone. Add your email once and you're in:",
    joinUrl(inv.token),
    '',
    `This link is only for your flat and works once, until ${fmtWeekday(inv.expires_at)}.`,
    '— ONE10 Food Committee',
  ].join('\n');
}

/** The message with a join link a family member shares with family. */
export function familyInviteText(inv: Invite): string {
  return [
    `${inv.name ? `${inv.name.split(' ')[0]} invited you` : "You're invited"} to our family at flat ${inv.flat} on the ONE10 food portal.`,
    '',
    "Tap to see our meal bookings and passes and book for the family. Add your email once and you're in:",
    joinUrl(inv.token),
    '',
    `The link works once, until ${fmtWeekday(inv.expires_at)}.`,
  ].join('\n');
}

const whatsappUrl = (phone: string, text: string) => `https://wa.me/91${phone}?text=${encodeURIComponent(text)}`;

/**
 * Makes an invite and opens WhatsApp to the flat's mobile with the message ready to send. The tab opens before the
 * invite is made (still inside the tap), so the browser doesn't block it as a pop-up.
 */
export async function inviteOnWhatsApp(make: () => Promise<Invite>, phone: string): Promise<Invite> {
  const tab = window.open('', '_blank');
  try {
    const inv = await make();
    const url = whatsappUrl(inv.phone ?? phone, inviteText(inv));
    if (tab) tab.location.href = url;
    else location.href = url;
    return inv;
  } catch (e) {
    tab?.close();
    throw e;
  }
}

export const inviteLive = (s: InviteStatus | undefined, now = Date.now()) =>
  !!s && !s.used_at && new Date(s.expires_at).getTime() > now;

/** "Invited 5 Oct, 6:40 PM · works till Mon 19 Oct", "Joined … · rina@…", or that it ran out. */
export function inviteStatusText(s: InviteStatus | undefined, now = Date.now()): string | null {
  if (!s) return null;
  if (s.used_at) return `Joined ${fmtDateTime(s.used_at)}${s.used_by ? ` · ${s.used_by}` : ''}`;
  if (inviteLive(s, now)) return `Invited ${fmtDateTime(s.created_at)} · works till ${fmtWeekday(s.expires_at)}`;
  return `The invite from ${fmtDateTime(s.created_at)} was not used`;
}

/** The join link as a big QR for the family to scan with their own phone (help desk, counter). */
export function JoinQrModal({ inv, onClose }: { inv: Invite; onClose: () => void }) {
  return (
    <Modal label={`Join QR for flat ${inv.flat}`} onClose={onClose}>
      <p className="jq-eyebrow">Flat {inv.flat} · {inv.name}</p>
      <QrCanvas value={joinUrl(inv.token)} size={480} />
      <p className="jq-h">Scan with your phone camera</p>
      <p className="jq-sub">Add your email once to get your meal passes, live updates and meal alerts. Works once, until {fmtWeekday(inv.expires_at)}.</p>
      <button className="btn ghost block" onClick={onClose}>Done</button>
    </Modal>
  );
}

/** On a flat with no email: send the family a join link on WhatsApp, or show it as a QR. */
export function InviteFamily({ flatId, phone }: { flatId: string; phone: string | null }) {
  const toast = useToast();
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['invites', flatId], queryFn: () => api.committeeInvites([flatId]) });
  const [qr, setQr] = useState<Invite | null>(null);
  const [busy, setBusy] = useState(false);
  const s = status.data?.[flatId];

  async function go(how: 'whatsapp' | 'qr') {
    setBusy(true);
    try {
      if (how === 'whatsapp' && phone) await inviteOnWhatsApp(() => api.committeeInvite(flatId), phone);
      else setQr(await api.committeeInvite(flatId));
      void qc.invalidateQueries({ queryKey: ['invites'] });
    } catch (e) {
      toast(errorText(e), { tone: 'bad' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="invite">
      <p className="invite-h">Invite them to the portal</p>
      <p className="fine">
        For a family that paid in cash, or has no UTR. A one-time link: they tap it, add their email, and they're in.
        {s && inviteLive(s) ? ' A new invite replaces the last one.' : ''}
      </p>
      <div className="row">
        {phone && <button className="btn primary sm" disabled={busy} onClick={() => void go('whatsapp')}>Invite on WhatsApp</button>}
        <button className="btn ghost sm" disabled={busy} onClick={() => void go('qr')}>Show join QR</button>
      </div>
      {s && <p className={`fine${s.used_at ? ' invite-joined' : ''}`}>{inviteStatusText(s)}</p>}
      {qr && <JoinQrModal inv={qr} onClose={() => setQr(null)} />}
    </div>
  );
}
